import type { EngineLabels, EngineStep, PopoverAlign, PopoverSide, TourEngine } from './tourEngine';

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- the Vuex store is plain JavaScript: its state and getters carry no types
type Untyped = any;

export type TourContext = {
    state: Untyped;
    getters: Untyped;
    dispatch(action: string, payload?: unknown): unknown;
    commit(mutation: string, payload?: unknown): void;
};

export type TourStep = {
    id: string;
    element?: string;
    /** Where the popover sits relative to the element; driver picks a side that fits when unset. */
    side?: PopoverSide;
    align?: PopoverAlign;
    title: string;
    text: string;
    /** Prepares the state the step talks about: dispatch and commit only. */
    enter?(ctx: TourContext): void;
    /** A step that has nothing to show in this state, checked before its `enter` runs. */
    skipWhen?(state: Untyped, getters: Untyped): boolean;
    /** Present on an interactive step: the step advances when this turns true. */
    advanceWhen?(state: Untyped, getters: Untyped): boolean;
    /** Undoes what the step asked for when the operator skips it instead: dispatch and commit only. */
    onSkip?(ctx: TourContext): void;
    /** Freezes the highlighted control on a narrated step whose click would derail the script. */
    disableInteraction?: boolean;
    /** Whether an editor that stays closed during this step aborts the tour. Defaults to true once the runner owns one. */
    expectsEditor?: boolean;
};

export type TourScript = {
    steps: TourStep[];
    /** The step whose `enter` opens the practice piece: from it on, the runner owns whatever new piece is open. */
    ownsEditorFrom: string;
    teardown(ctx: TourContext): void;
};

export type TourStore = TourContext & {
    watch<T>(getter: (state: Untyped, getters: Untyped) => T, callback: (value: T) => void): () => void;
};

export type TourOutcome = 'completed' | 'closed' | 'aborted';

export type RunningTour = { stop(): void };

export const TOUR_KEYBOARD_HOLDER = 'addOnsTour';

// A piece can close and another open within a tick or two. The abort waits
// this long so that replacement reads as an adoption.
const SETTLE_MS = 150;

type Options = {
    store: TourStore;
    engine: TourEngine;
    labels: EngineLabels;
    onEnd(outcome: TourOutcome): void;
};

function toEngineStep(step: TourStep): EngineStep {
    return {
        element: step.element,
        title: step.title,
        text: step.text,
        interactive: typeof step.advanceWhen === 'function',
        disableInteraction: step.disableInteraction,
        side: step.side,
        align: step.align,
    };
}

export function runTour(script: TourScript, { store, engine, labels, onEnd }: Options): RunningTour {
    const ctx: TourContext = {
        state: store.state,
        getters: store.getters,
        dispatch: (action, payload) => store.dispatch(action, payload),
        commit: (mutation, payload) => store.commit(mutation, payload),
    };
    const ownsFrom = script.steps.findIndex(step => step.id === script.ownsEditorFrom);
    const lastIndex = script.steps.length - 1;

    let index = -1;
    // From the call that moves driver until it reports that step shown: the
    // popover on screen is still the old one, so its Next or Skip is stale.
    let moving = false;
    let ended = false;
    let owned = false;
    let unwatchAdvance: (() => void) | null = null;
    let settleTimer: ReturnType<typeof setTimeout> | null = null;

    // The steps shown so far, for the count the popover reads: driver's own
    // counts the steps the tour passes over, so "8 of 15" jumped to "12 of 15"
    // on a phone where the page-turn steps are skipped.
    const shown = new Set<number>();
    const skipped = (i: number) => script.steps[i].skipWhen?.(store.state, store.getters) ?? false;
    const progressFor = (i: number) => {
        const current = [...shown].filter(j => j < i).length + 1;
        let ahead = 0;
        for (let j = i + 1; j <= lastIndex; j += 1) if (!skipped(j)) ahead += 1;
        return { current, total: current + ahead };
    };

    const ownsEditor = () => owned;
    const editorOpen = () => store.state.ui?.addOnsPanel?.editor != null;
    const panelOpen = () => Boolean(store.state.ui?.addOnsPanel?.opened);
    const expectsEditor = () => ownsEditor() && (script.steps[index]?.expectsEditor ?? true);

    const clearAdvance = () => {
        unwatchAdvance?.();
        unwatchAdvance = null;
    };
    const clearSettle = () => {
        if (settleTimer) clearTimeout(settleTimer);
        settleTimer = null;
    };
    const armSettle = () => {
        clearSettle();
        settleTimer = setTimeout(() => {
            settleTimer = null;
            if (ended || !expectsEditor()) return;
            if (!editorOpen() || !panelOpen()) end('aborted');
        }, SETTLE_MS);
    };

    const end = (outcome: TourOutcome) => {
        if (ended) return;
        ended = true;
        clearAdvance();
        clearSettle();
        unwatchAbort();
        unwatchLayout();
        try {
            if (ownsEditor() && editorOpen()) script.teardown(ctx);
        } finally {
            store.commit('resumeKeyboardNavigation', TOUR_KEYBOARD_HOLDER);
            engine.destroy();
            onEnd(outcome);
        }
    };

    /** Puts the reader in the state step `i` talks about, before driver shows it. */
    const prepare = (i: number) => {
        clearAdvance();
        clearSettle();
        index = i;
        // Once the tour has opened its piece it owns whatever new piece is
        // open, whichever step is showing.
        if (ownsFrom !== -1 && i >= ownsFrom) owned = true;
        script.steps[i].enter?.(ctx);
        // A step that needs the piece checks it is there, not only that it went away.
        if (expectsEditor() && !(editorOpen() && panelOpen())) armSettle();
    };

    const satisfied = (i: number) => script.steps[i].advanceWhen?.(store.state, store.getters) ?? false;

    /**
     * Prepares the steps from `from` on and returns the first one still to
     * show, or past the last step when none is. driver resolves a step's
     * anchor when it moves to it, so the state the step needs has to exist
     * first; a step already satisfied is passed over here so driver never
     * waits for an anchor that will not appear.
     */
    const firstToShow = (from: number) => {
        let target = from;
        while (target <= lastIndex) {
            if (skipped(target)) {
                target += 1;
                continue;
            }
            prepare(target);
            if (!satisfied(target)) break;
            target += 1;
        }
        return target;
    };

    const advance = () => {
        clearAdvance();
        const target = firstToShow(index + 1);
        if (target > lastIndex) {
            end('completed');
            return;
        }
        moving = true;
        engine.moveTo(target);
    };

    // driver reports the steps it passes through on the way; only the one the
    // runner prepared is the step on screen.
    const showStep = (i: number) => {
        if (ended || i !== index) return;
        moving = false;
        shown.add(i);
        clearAdvance();
        const predicate = script.steps[i]?.advanceWhen;
        if (!predicate) return;
        // The state may have moved on during driver's transition.
        if (predicate(store.state, store.getters)) {
            // driver calls onHighlighted from inside its transition frame and
            // rewrites the active step once the hook returns; a microtask lands after that.
            void Promise.resolve().then(() => {
                if (!ended && index === i) advance();
            });
            return;
        }
        unwatchAdvance = store.watch(
            (state, getters) => predicate(state, getters),
            holds => {
                if (holds) advance();
            }
        );
    };

    // One watcher over the three things that end a tour from outside: the
    // rights, the panel and the piece. The piece may come back within the
    // settle delay, so it is checked again before aborting.
    const unwatchAbort = store.watch(
        (state, getters) => ({
            can: Boolean(getters.canEditAddOns),
            panel: Boolean(state.ui?.addOnsPanel?.opened),
            editor: state.ui?.addOnsPanel?.editor != null,
        }),
        ({ can, panel, editor }) => {
            if (ended) return;
            if (!can) {
                end('aborted');
                return;
            }
            if (!expectsEditor() || (panel && editor)) return;
            armSettle();
        }
    );

    // The phone sheet steps aside during a capture and comes back after it,
    // which moves every anchor the popover points at.
    const unwatchLayout = store.watch(
        (_state, getters) => Boolean(getters.addOnEditorAwaitsAnchor),
        () => engine.refresh()
    );

    store.commit('pauseKeyboardNavigation', TOUR_KEYBOARD_HOLDER);
    const first = firstToShow(0);
    if (first > lastIndex) {
        end('completed');
        return { stop: () => end('closed') };
    }
    const advanceFrom = (i: number) => {
        if (!moving && i === index) advance();
    };
    const skipFrom = (i: number) => {
        if (moving || i !== index) return;
        script.steps[i].onSkip?.(ctx);
        advance();
    };

    moving = true;
    engine.start(
        script.steps.map(toEngineStep),
        {
            onStepShown: showStep,
            onNext: advanceFrom,
            onSkip: skipFrom,
            onDone: () => end('completed'),
            onCloseRequested: () => end('closed'),
            progressFor,
        },
        labels,
        first
    );

    return { stop: () => end('closed') };
}
